import { useEffect, useMemo } from 'react';
import { Alert, Button, Form, Input, InputNumber, Select, Switch, type FormInstance } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  customParameterEntriesError, isParameterApplicable, MAX_CUSTOM_PARAMETERS,
  type CustomParameterDefinitions, type CustomParameterEntry,
} from '@/utils/jobCustomParameters';
import './customParameters.less';

export default function CustomParametersFields({
  form, taskTypeId, active, definitions, loading, error, onReload, promotedKeys,
}: {
  form: FormInstance;
  taskTypeId?: number;
  active: boolean;
  definitions: CustomParameterDefinitions;
  loading: boolean;
  error: boolean;
  onReload: () => void;
  /** 已提到表单顶层的参数 key：本块不再提供填写入口（见各 key 的顶层字段）。 */
  promotedKeys: readonly string[];
}) {
  const entries = Form.useWatch('customParameterEntries', form) as CustomParameterEntry[] | undefined;

  // 列表级错误也要跟随子字段修正；Ant Design 的 Form.List 不会自动校验嵌套变更。
  useEffect(() => {
    if (active && form.getFieldError('customParameterEntries').length) {
      void form.validateFields(['customParameterEntries']).catch(() => {});
    }
  }, [active, definitions, entries, error, form, loading, taskTypeId]);

  const applicable = useMemo(() => Object.entries(definitions)
    .filter(([key, definition]) => !promotedKeys.includes(key) && isParameterApplicable(definition, taskTypeId)),
  [definitions, taskTypeId, promotedKeys]);
  const selectedKeys = new Set((entries ?? []).map(entry => entry?.key));

  return (
    <div className="job-custom-parameters">
      <div className="job-custom-parameters-title">自定义参数</div>
      <div className="job-custom-parameter-hint">按需添加参数；未添加时使用任务的默认行为。</div>
      {error && <Alert showIcon type="info" message="自定义参数暂不可用，未使用自定义参数的任务仍可正常创建。" action={
        <Button size="small" onClick={onReload}>重试</Button>
      } />}
      <Form.List name="customParameterEntries" rules={[{
        validator: async (_, values: CustomParameterEntry[] | undefined) => {
          if (!values?.length) return;
          // 提到顶层的参数绝不能再从本块提交。Form.List 的 rules 挂在内部 Field name={[]} 上、
          // 行数据取自 store —— 在 fields.map 里跳过某行既不移出校验也不移出 store，
          // 只靠提交时合并去剥掉会变成"静默丢参"，所以必须在这里显式拒绝。
          const promoted = values.find(entry => entry?.key && promotedKeys.includes(entry.key));
          if (promoted) throw new Error(`参数 ${promoted.key} 请在表单上方单独填写，不要在此重复添加`);
          if (loading || error) throw new Error('自定义参数暂不可用，请重试加载或移除已添加的参数后继续');
          const validationError = customParameterEntriesError(values, definitions, taskTypeId);
          if (validationError) throw new Error(validationError);
        },
      }]}>
        {(fields, { add, remove }, { errors }) => (
          <>
            {fields.map(field => {
              const current = entries?.[field.name];
              const definition = current?.key && Object.prototype.hasOwnProperty.call(definitions, current.key) ? definitions[current.key] : undefined;
              const available = definition && isParameterApplicable(definition, taskTypeId);
              const options = applicable.map(([key, item]) => ({
                value: key, label: `${item.label} (${key})`, disabled: selectedKeys.has(key) && key !== current?.key,
              }));
              if (current?.key && !options.some(option => option.value === current.key)) {
                options.unshift({ value: current.key, label: `${current.key}（当前不可用）`, disabled: true });
              }
              return (
                <div className="job-custom-parameter-row" key={field.key}>
                  <div className="job-custom-parameter-inputs">
                    <Form.Item name={[field.name, 'key']} label="参数" style={{ marginBottom: 8 }}>
                      <Select
                        showSearch optionFilterProp="label" placeholder="选择参数" loading={loading}
                        options={options} disabled={loading || error}
                        onChange={key => form.setFieldValue(['customParameterEntries', field.name, 'value'],
                          definitions[key].inputType === 'boolean' ? false : definitions[key].inputType === 'text' ? '' : undefined)}
                      />
                    </Form.Item>
                    <Form.Item name={[field.name, 'value']} label={definition?.label || '参数值'}
                      valuePropName={definition?.inputType === 'boolean' ? 'checked' : 'value'} style={{ marginBottom: 0 }}>
                      {definition?.inputType === 'select' ? <Select placeholder="选择一个选项" disabled={!available} allowClear
                        options={(definition.options ?? []).map(option => ({ value: option, label: option }))} />
                        : definition?.inputType === 'number' ? <InputNumber style={{ width: '100%' }} placeholder="输入数字" disabled={!available} />
                          : definition?.inputType === 'boolean' ? <Switch checkedChildren="开启" unCheckedChildren="关闭" disabled={!available} />
                            : <Input placeholder="输入参数值" maxLength={4096} disabled={!available} />}
                    </Form.Item>
                    {definition?.description && <div className="job-custom-parameter-hint">{definition.description}</div>}
                    {current?.key && !loading && !error && !available && <Alert showIcon type="warning" message="此参数已停用、删除或不适用于当前任务类型，请修改或移除。" />}
                  </div>
                  <Button type="text" danger icon={<DeleteOutlined />} aria-label={`移除参数 ${current?.key || field.name + 1}`} title="移除此参数" onClick={() => remove(field.name)} />
                </div>
              );
            })}
            <Button type="dashed" icon={<PlusOutlined />} loading={loading}
              disabled={error || fields.length >= MAX_CUSTOM_PARAMETERS || !applicable.some(([key]) => !selectedKeys.has(key))}
              onClick={() => add({})}>添加自定义参数</Button>
            {!loading && !error && applicable.length === 0 && <span className="job-custom-parameter-hint">
              {entries?.length ? '暂无可用参数，请移除已添加的参数后继续' : '暂无可用参数，可直接创建任务'}
            </span>}
            <Form.ErrorList errors={errors} />
          </>
        )}
      </Form.List>
    </div>
  );
}
